import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Map, NavigationControl } from '@vis.gl/react-maplibre';
import type { MapEvent, MapRef, StyleSpecification } from '@vis.gl/react-maplibre';
import { MapBearingContext, MapZoomContext } from './MapBearingContext';
import { CameraController } from './CameraController';
import { NarrativeOverlay } from './NarrativeOverlay';
import { TrafficLayer } from './TrafficLayer';
import type { FlowTileStyle } from '../../types/traffic';

const STREET_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      ],
      tileSize: 256,
      maxzoom: 19,
    }
  },
  layers: [{ id: 'satellite', type: 'raster', source: 'esri-satellite' }]
};

const HIDDEN_LAYERS = [
  'park', 'park_outline',
  'landcover_wood', 'landcover_grass', 'landcover_wetland',
  'poi_r20', 'poi_r7', 'poi_r1'
];

interface MapViewProps {
  children?: ReactNode;
  center?: [number, number];
  zoom?: number;
  bearing?: number;
  pitch?: number;
  maxBounds?: [[number, number], [number, number]];
  minZoom?: number;
  maxZoom?: number;
  cameraControl?: boolean;
}

const DEFAULT_CENTER: [number, number] = [2.1651, 41.3908];
const DEFAULT_ZOOM = 18.5;
const DEFAULT_BEARING = -45;
const DEFAULT_PITCH = 50;

export function MapView({
  children,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  bearing = DEFAULT_BEARING,
  pitch = DEFAULT_PITCH,
  maxBounds,
  minZoom,
  maxZoom,
  cameraControl = false,
}: MapViewProps) {
  const [mapBearing, setMapBearing] = useState(bearing);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [satellite, setSatellite] = useState(false);
  const [flowStyle, setFlowStyle] = useState<FlowTileStyle>('relative0');
  const [mapReady, setMapReady] = useState(false);
  const [narrativeText, setNarrativeText] = useState<string | null>(null);
  const mapRef = useRef<MapRef>(null);

  const mapStyle = useMemo(() => satellite ? SATELLITE_STYLE : STREET_STYLE, [satellite]);

  const handleLoad = useCallback((e: MapEvent) => {
    setMapReady(true);
    if (!satellite) {
      for (const id of HIDDEN_LAYERS) {
        if (e.target.getLayer(id)) e.target.removeLayer(id);
      }
    }
  }, [satellite]);

  const hideStreetLayers = useCallback((e: MapEvent) => {
    if (satellite) return;
    const map = e.target;
    for (const id of HIDDEN_LAYERS) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
  }, [satellite]);

  return (
    <div className="relative w-full h-full">
      <button
        onClick={() => setSatellite(s => !s)}
        className="absolute bottom-24 left-3 z-50 bg-white/90 hover:bg-white shadow-md rounded px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors cursor-pointer"
      >
        {satellite ? 'Mapa' : 'Satélite'}
      </button>
      {import.meta.env.VITE_TOMTOM_API_KEY && (
        <div className="absolute bottom-16 left-3 z-50 flex bg-white/90 shadow-md rounded overflow-hidden text-[10px] font-medium text-gray-700">
          {([['relative0', 'Relativo'], ['absolute', 'Absoluto'], ['relative-delay', 'Retraso']] as const).map(([style, label]) => (
            <button
              key={style}
              onClick={() => setFlowStyle(style)}
              className={`px-2 py-1.5 transition-colors cursor-pointer ${
                flowStyle === style
                  ? 'bg-blue-500 text-white'
                  : 'hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <Map
        ref={mapRef}
        onLoad={handleLoad}
        onStyleData={hideStreetLayers}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom,
          bearing,
          pitch
        }}
        onMove={(e) => {
          setMapBearing(e.viewState.bearing);
          setMapZoom(e.viewState.zoom);
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        maxBounds={maxBounds}
        minZoom={minZoom}
        maxZoom={maxZoom}
        attributionControl={false}
      >
        <NavigationControl position="bottom-right" />
        {cameraControl && <CameraController mapRef={mapRef} ready={mapReady} onNarrative={setNarrativeText} />}
        <TrafficLayer flowStyle={flowStyle} />
        <MapBearingContext.Provider value={mapBearing}>
          <MapZoomContext.Provider value={mapZoom}>
            {children}
          </MapZoomContext.Provider>
        </MapBearingContext.Provider>
      </Map>
      {cameraControl && <NarrativeOverlay text={narrativeText} />}
    </div>
  );
}
